import { createWorkerPayloadCodec } from './codecs';
import type { WorkerPayloadCodec } from './codecs';
import type { WorkerContract, WorkerEventType, WorkerManifest, WorkerRequestType } from './contract';
import type { WorkerClientMetadata, WorkerFrame } from './envelope';
import { WorkerDiedError, WorkerRequestError, WorkerSpawnError, deserializeWorkerError } from './errors';
import { encodeWorkerStatePayload } from './stateRegistry';
import type { WorkerStateKey, WorkerStatePayloads, WorkerStateRelay } from './stateRegistry';
import type { WorkerFramePipe, WorkerTransport } from './transports/types';
/*
 * The main-thread worker client (ADR 0016): `connectWorker` derives everything from the same
 * contract and manifest the worker runs — it resolves a `WorkerStateRelay` for each manifest
 * key (and only those: resolution is the side effect being avoided under 0015's
 * first-resolution-instantiates semantics), assembles the single `init` frame, and exposes the
 * typed request/event surface with spawn-deadline, ping-based death detection, and the
 * abort-first teardown idiom.
 */
import { signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { TypedContainerLike } from '@thinkalpha/common/ioc/TypedContainer.js';
import { deferred } from '@thinkalpha/ui-core/deferred';
import { runtimeEnvTriple } from 'src/lib/config/appConfig';

/**
 * The per-key relay binding tokens. A spawning container provides `WorkerStateRelay:<key>` for
 * each key its workers may compose; `connectWorker` resolves exactly the manifest's keys.
 */
export type WorkerStateRelayBindings = {
    [K in WorkerStateKey as `WorkerStateRelay:${K}`]: WorkerStateRelay<K>;
};

export type WorkerClientState = 'connecting' | 'ready' | 'errored' | 'disposed';

export interface WorkerClientEvents<E extends WorkerContract['events']> {
    on<K extends keyof E & string>(type: K, handler: (payload: E[K]) => void): Disposable;
}

export interface WorkerClient<C extends WorkerContract> extends Disposable {
    /** Rejects on spawn failure or boot-error. */
    readonly ready: Promise<void>;
    /**
     * Never stuck at 'connecting' once unusable: spawn/boot failure and detected death
     * transition to 'errored' (terminal today; shaped for future client-driven recovery),
     * disposal to 'disposed'.
     */
    readonly state: ReadonlySignal<WorkerClientState>;
    request<K extends WorkerRequestType<C>>(
        type: K,
        payload: C['requests'][K]['request'],
        opts?: { signal?: AbortSignal },
    ): Promise<C['requests'][K]['response']>;
    readonly events: WorkerClientEvents<C['events']>;
}

export interface ConnectWorkerOptions {
    /** Rides in `init.client`; per-client Datadog/tab correlation on multi-client workers. */
    readonly metadata?: WorkerClientMetadata;
    /**
     * Whether `request()` calls issued before `ready` settles buffer until it resolves or fail
     * fast — per-worker policy, deliberately not baked into the shared machinery. Default 'buffer'.
     */
    readonly preReady?: 'buffer' | 'fail';
    /** `ready` must arrive within this deadline or the spawn is declared failed. */
    readonly spawnDeadlineMs?: number;
    /** Liveness probe cadence; two consecutive missed pongs transition to 'errored'. */
    readonly pingIntervalMs?: number;
}

const DEFAULT_SPAWN_DEADLINE_MS = 30_000;
const DEFAULT_PING_INTERVAL_MS = 10_000;

interface PendingRequest {
    settleOk(data: unknown): void;
    settleError(error: unknown): void;
}

class WorkerClientImpl<C extends WorkerContract> implements WorkerClient<C> {
    readonly #stack: DisposableStack;
    readonly #teardown = new AbortController();
    readonly #codec: WorkerPayloadCodec;
    readonly #pipe: WorkerFramePipe;
    readonly #stateSignal = signal<WorkerClientState>('connecting');
    readonly #pending = new Map<number, PendingRequest>();
    readonly #preReadyQueue: Array<() => void> = [];
    readonly #eventHandlers = new Map<string, Set<(decoded: unknown) => void>>();
    readonly #readyDeferred = deferred<void>();
    readonly #preReadyPolicy: 'buffer' | 'fail';
    readonly #pingIntervalMs: number;
    #nextRequestId = 1;
    #pingSeq = 0;
    #outstandingPings = 0;
    #terminalError: Error | undefined;

    readonly events: WorkerClientEvents<C['events']> = {
        on: (type, handler) => this.#on(type, handler),
    };

    constructor(
        container: TypedContainerLike<WorkerStateRelayBindings>,
        manifest: WorkerManifest<C>,
        transport: WorkerTransport,
        options: ConnectWorkerOptions,
    ) {
        this.#codec = createWorkerPayloadCodec(manifest);
        this.#preReadyPolicy = options.preReady ?? 'buffer';
        this.#pingIntervalMs = options.pingIntervalMs ?? DEFAULT_PING_INTERVAL_MS;

        // Under-provisioning fails synchronously at the call site, before the worker script
        // even loads — the line that spawned the worker is the line that reads the error.
        for (const key of manifest.state) {
            if (!container.isBound(`WorkerStateRelay:${key}`)) {
                throw new Error(
                    `connectWorker: no WorkerStateRelay is bound for composed state key '${key}'. ` +
                        'The spawning container must provide a relay for every key the worker manifest lists.',
                );
            }
        }

        // Everything from here accumulates on a local stack; construction failure past
        // transport.connect() would otherwise leak the spawned worker and any relay
        // subscriptions already made. On success the stack moves into the instance.
        using stack = new DisposableStack();
        this.#pipe = stack.use(transport.connect());
        stack.use(this.#pipe.onFrame((frame) => this.#onFrame(frame)));
        stack.use(this.#pipe.onClose((error) => this.#onClose(error)));

        // Snapshot and subscription are one atomic operation: connect() emits the current value
        // synchronously (captured into init.state); later emissions buffer until init is on the
        // wire — collecting a later key's snapshot can trigger an earlier key's next emission —
        // then stream as pushes. Nothing precedes init by construction.
        const snapshots: Record<string, unknown> = {};
        let initSent = false;
        const bufferedPushes: WorkerFrame[] = [];
        for (const key of manifest.state) {
            const relay = container.get(`WorkerStateRelay:${key}`);
            if (relay.key !== key) {
                throw new Error(
                    `connectWorker: relay bound under 'WorkerStateRelay:${key}' reports key '${relay.key}'`,
                );
            }
            let snapshotCaptured = false;
            stack.use(
                relay.connect((payload: WorkerStatePayloads[WorkerStateKey]) => {
                    if (!initSent) {
                        // Still constructing: an encode throw propagates out of the constructor
                        // and the local `using` stack releases everything (fail fast).
                        const encoded = encodeWorkerStatePayload(key, payload);
                        if (!snapshotCaptured) {
                            snapshotCaptured = true;
                            snapshots[key] = encoded;
                        } else {
                            bufferedPushes.push({ kind: 'push', key, payload: encoded });
                        }
                        return;
                    }
                    // Streaming: the throw must not escape into the emitting service, and the
                    // client must not go silently stale — an unencodable payload is terminal.
                    let encoded: unknown;
                    try {
                        encoded = encodeWorkerStatePayload(key, payload);
                    } catch (error) {
                        this.#fail(
                            new Error(`Worker state relay '${key}' produced a payload its schema rejects`, {
                                cause: error,
                            }),
                        );
                        return;
                    }
                    this.#send({ kind: 'push', key, payload: encoded });
                }),
            );
            if (!snapshotCaptured) {
                throw new Error(
                    `connectWorker: the '${key}' relay did not emit synchronously from connect(); ` +
                        'relays must have signal-effect semantics (current value first, then changes)',
                );
            }
        }

        // Direct pipe sends: #send guards on the instance stack, which is only assigned below.
        this.#pipe.send({
            kind: 'init',
            clientId: crypto.randomUUID(),
            client: options.metadata ?? {},
            env: runtimeEnvTriple,
            state: snapshots,
        });
        initSent = true;
        for (const frame of bufferedPushes.splice(0)) {
            this.#pipe.send(frame);
        }

        const spawnDeadlineMs = options.spawnDeadlineMs ?? DEFAULT_SPAWN_DEADLINE_MS;
        const spawnTimer = setTimeout(() => {
            if (this.#stateSignal.value === 'connecting') {
                this.#fail(new WorkerSpawnError(`Worker did not become ready within ${spawnDeadlineMs}ms`));
            }
        }, spawnDeadlineMs);
        stack.defer(() => clearTimeout(spawnTimer));

        // Surface spawn failures via state/held requests even when nobody awaits `ready`.
        this.#readyDeferred.promise.catch(() => undefined);

        // Construction succeeded: ownership of every accumulated resource transfers to the
        // instance; the `using` disposal at scope exit finds an empty, moved-from stack.
        this.#stack = stack.move();
    }

    get ready(): Promise<void> {
        return this.#readyDeferred.promise;
    }

    get state(): ReadonlySignal<WorkerClientState> {
        return this.#stateSignal;
    }

    request<K extends WorkerRequestType<C>>(
        type: K,
        payload: C['requests'][K]['request'],
        opts?: { signal?: AbortSignal },
    ): Promise<C['requests'][K]['response']> {
        if (this.#stateSignal.value === 'disposed') {
            return Promise.reject(new DOMException('Worker client disposed', 'AbortError'));
        }
        if (this.#terminalError) {
            return Promise.reject(this.#terminalError);
        }
        if (opts?.signal?.aborted) {
            return Promise.reject(asAbortError(opts.signal.reason));
        }
        if (this.#stateSignal.value !== 'ready' && this.#preReadyPolicy === 'fail') {
            return Promise.reject(new WorkerSpawnError('Worker client is not ready (fail-fast preReady policy)'));
        }

        const id = this.#nextRequestId++;
        const { promise, resolve, reject } = deferred<C['requests'][K]['response']>();

        const settle = (): void => {
            this.#pending.delete(id);
            opts?.signal?.removeEventListener('abort', onAbort);
        };
        const onAbort = (): void => {
            // Cancellation is local: the caller's wait rejects, no cancel frame is sent, and the
            // worker's late response frame is discarded. Side effects are not rolled back.
            settle();
            reject(asAbortError(opts?.signal?.reason));
        };
        opts?.signal?.addEventListener('abort', onAbort, { once: true });

        this.#pending.set(id, {
            settleOk: (data) => {
                settle();
                try {
                    // Wire-boundary: validated by the contract codec (zod) or a declared
                    // passthrough (structuredClone).
                    resolve(this.#codec.decodeResponse(type, data) as C['requests'][K]['response']);
                } catch (error) {
                    reject(error);
                }
            },
            settleError: (error) => {
                settle();
                reject(error);
            },
        });

        const send = (): void => {
            let encoded: unknown;
            try {
                encoded = this.#codec.encodeRequest(type, payload);
            } catch (error) {
                this.#pending.get(id)?.settleError(error);
                return;
            }
            this.#send({ kind: 'request', id, type, payload: encoded });
        };

        if (this.#stateSignal.value === 'ready') {
            send();
        } else {
            this.#preReadyQueue.push(send);
        }

        return promise;
    }

    #on<K extends WorkerEventType<C>>(type: K, handler: (payload: C['events'][K]) => void): Disposable {
        let handlers = this.#eventHandlers.get(type);
        if (!handlers) {
            handlers = new Set();
            this.#eventHandlers.set(type, handlers);
        }
        const set = handlers;
        const wrapped = (decoded: unknown): void => {
            // Wire-boundary: validated by the contract codec (zod) or a declared passthrough.
            handler(decoded as C['events'][K]);
        };
        set.add(wrapped);
        return {
            [Symbol.dispose]: () => {
                set.delete(wrapped);
            },
        };
    }

    #send(frame: WorkerFrame): void {
        if (this.#stack.disposed || this.#terminalError) {
            return;
        }
        this.#pipe.send(frame);
    }

    #onFrame(frame: WorkerFrame): void {
        switch (frame.kind) {
            case 'ready':
                if (this.#stateSignal.value === 'connecting') {
                    this.#stateSignal.value = 'ready';
                    this.#readyDeferred.resolve();
                    this.#startPingLoop();
                    for (const send of this.#preReadyQueue.splice(0)) {
                        send();
                    }
                }
                return;
            case 'boot-error':
                if (this.#stateSignal.value === 'connecting') {
                    this.#fail(
                        new WorkerSpawnError('Worker boot failed', { cause: deserializeWorkerError(frame.error) }),
                    );
                }
                return;
            case 'response': {
                const pending = this.#pending.get(frame.id);
                if (!pending) {
                    // Locally cancelled: the late response is discarded by contract.
                    return;
                }
                if (frame.ok) {
                    pending.settleOk(frame.payload);
                } else {
                    pending.settleError(new WorkerRequestError(frame.error));
                }
                return;
            }
            case 'event': {
                const handlers = this.#eventHandlers.get(frame.type);
                if (!handlers?.size) {
                    return;
                }
                let decoded: unknown;
                try {
                    decoded = this.#codec.decodeEvent(frame.type, frame.payload);
                } catch (error) {
                    // eslint-disable-next-line no-console -- protocol violation must be loud; the client has no Logger binding
                    console.error(`[worker-client] dropped invalid '${frame.type}' event payload`, error);
                    return;
                }
                for (const handler of [...handlers]) {
                    handler(decoded);
                }
                return;
            }
            case 'pong':
                this.#outstandingPings = 0;
                return;
            default:
                // init/push/request/ping never flow worker→client.
                // eslint-disable-next-line no-console -- protocol violation must be loud; the client has no Logger binding
                console.error(`[worker-client] dropped unexpected inbound '${frame.kind}' frame`);
        }
    }

    #onClose(error?: unknown): void {
        // Abort-first teardown gives intentional disposal precedence: the closure that
        // dispose() itself causes finds this handler short-circuited, so it cannot re-enter
        // failure mapping and misreport 'errored'.
        if (this.#teardown.signal.aborted) {
            return;
        }
        if (this.#stateSignal.value === 'connecting') {
            this.#fail(new WorkerSpawnError('Worker closed before becoming ready', { cause: error }));
        } else {
            this.#fail(new WorkerDiedError('Worker pipe closed unexpectedly', { cause: error }));
        }
    }

    #startPingLoop(): void {
        const interval = setInterval(() => {
            if (this.#teardown.signal.aborted || this.#terminalError) {
                return;
            }
            if (this.#outstandingPings >= 2) {
                this.#fail(
                    new WorkerDiedError(
                        `Worker missed ${this.#outstandingPings} pings (~${2 * this.#pingIntervalMs}ms silent)`,
                    ),
                );
                return;
            }
            this.#outstandingPings += 1;
            this.#send({ kind: 'ping', seq: this.#pingSeq++ });
        }, this.#pingIntervalMs);
        this.#stack.defer(() => clearInterval(interval));
    }

    /**
     * Terminal failure: spawn failure or detected death. Absorbing; disposal still supersedes.
     * Every terminal transition disposes the affected scope (ADR 0016 Lifecycle): relays,
     * timers, and the pipe must not outlive the failure waiting for a dispose() that may never
     * come.
     */
    #fail(error: Error): void {
        if (this.#terminalError || this.#stateSignal.value === 'disposed') {
            return;
        }
        this.#terminalError = error;
        this.#stateSignal.value = 'errored';
        this.#readyDeferred.reject(error);
        this.#preReadyQueue.length = 0;
        for (const pending of [...this.#pending.values()]) {
            pending.settleError(error);
        }
        this.#pending.clear();
        // Retire when the pipe supports it — the stack disposal below would merely return a
        // pooled lease (a zombie worker) to the pool; a retired lease's disposal is a no-op.
        this.#pipe.retire?.();
        this.#stack.dispose();
    }

    [Symbol.dispose](): void {
        if (this.#stateSignal.value === 'disposed') {
            return;
        }
        this.#teardown.abort();
        const abortError = new DOMException('Worker client disposed', 'AbortError');
        this.#readyDeferred.reject(abortError);
        for (const pending of [...this.#pending.values()]) {
            pending.settleError(abortError);
        }
        this.#pending.clear();
        this.#preReadyQueue.length = 0;
        this.#eventHandlers.clear();
        if (!this.#stack.disposed) {
            this.#stack.dispose();
        }
        this.#stateSignal.value = 'disposed';
    }
}

const asAbortError = (reason: unknown): unknown =>
    reason ?? new DOMException('This operation was aborted', 'AbortError');

/**
 * Spawn (via the transport) and connect to a worker. Resolves a relay for each manifest key —
 * and only those — from the caller's container; missing relays throw synchronously, here.
 */
export const connectWorker = <C extends WorkerContract>(
    container: TypedContainerLike<WorkerStateRelayBindings>,
    manifest: WorkerManifest<C>,
    transport: WorkerTransport,
    options: ConnectWorkerOptions = {},
): WorkerClient<C> => new WorkerClientImpl(container, manifest, transport, options);
